/**
 * ReportBuilder
 * Reusable component for metric and view selection in mobile view.
 * 
 * Features:
 * - View selector (Region/Unit, Market/POD, Location)
 * - Metric selection with pills (max 3)
 * - Reset and Apply actions
 * - Expandable/collapsible interface
 */

import React from 'react';
import {
	Platform,
	View,
	Text,
	StyleSheet,
	TouchableOpacity,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ViewType = 'region' | 'market' | 'location';

export interface MetricDefinition {
	id: string;
	label: string;
}

export interface ViewTab {
	id: ViewType;
	label: string;
}

export interface ReportBuilderProps {
	/** Whether the builder is expanded */
	readonly isExpanded: boolean;
	/** Currently selected view type */
	readonly selectedView: ViewType;
	/** Array of selected metric IDs */
	readonly selectedMetrics: string[];
	/** Available metrics to choose from */
	readonly availableMetrics: MetricDefinition[];
	/** Available view tabs */
	readonly viewTabs: ViewTab[];
	/** Maximum number of metrics allowed */
	readonly maxMetrics?: number;
	/** When true, unselected metrics are disabled once the max is reached */
	readonly disableUnselectedMetrics?: boolean;
	/** Called when the header is tapped to toggle expansion */
	readonly onToggle: () => void;
	/** Called when a view tab is selected */
	readonly onViewChange: (view: ViewType) => void;
	/** Called when a metric is toggled */
	readonly onMetricToggle: (metricId: string) => void;
	/** Called when Reset is pressed */
	readonly onReset: () => void;
	/** Called when Apply is pressed */
	readonly onApply: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ReportBuilder: React.FC<ReportBuilderProps> = ({
	isExpanded,
	selectedView,
	selectedMetrics,
	availableMetrics,
	viewTabs,
	maxMetrics = 3,
	disableUnselectedMetrics = false,
	onToggle,
	onViewChange,
	onMetricToggle,
	onReset,
	onApply,
}) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
	const selectedCount = selectedMetrics.length;

	return (
		<View style={[styles.container, isDark && { backgroundColor: colors.background.surface }]}>
			{/* Header */}
			<TouchableOpacity
				style={styles.header}
				onPress={onToggle}
				activeOpacity={0.7}
			>
				<View style={styles.headerLeft}>
					<View style={styles.iconContainer}>
						<MaterialIcons name="bar-chart" size={24} color="#FFFFFF" />
					</View>
					<View style={styles.headerTextContainer}>
						<Text style={styles.headerTitle}>Report Builder</Text>
						<Text style={[styles.headerSubtitle, isDark && { color: colors.text.secondary }]}>
							{selectedCount} {selectedCount === 1 ? 'metric' : 'metrics'} selected
						</Text>
					</View>
				</View>
				<MaterialIcons
					name={isExpanded ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
					size={24}
					color="#005CBC"
				/>
			</TouchableOpacity>

			{/* Expanded Content */}
			{isExpanded && (
				<View style={[styles.content, isDark && { borderTopColor: colors.border.divider }]}>
					{/* View By Section */}
					<Text style={[styles.sectionLabel, isDark && { color: colors.text.secondary }]}>VIEW BY</Text>
					<View style={[styles.viewSelector, isDark && { backgroundColor: colors.background.page }]}>
						{viewTabs.map((tab) => (
							<TouchableOpacity
								key={tab.id}
								style={[
									styles.viewTab,
									selectedView === tab.id && styles.viewTabActive,
									selectedView === tab.id && isDark && { backgroundColor: colors.background.elevated },
								]}
								onPress={() => onViewChange(tab.id)}
								activeOpacity={0.7}
							>
								<Text
									style={[
										styles.viewTabText,
										isDark && { color: colors.text.secondary },
										selectedView === tab.id && styles.viewTabTextActive,
									]}
								>
									{tab.label}
								</Text>
							</TouchableOpacity>
						))}
					</View>

					{/* Instruction */}
					<Text style={[styles.instruction, isDark && { color: colors.text.disabled }]}>
						Please select up to 3 metrics. To view more please use a desktop device.
					</Text>

					{/* Metric Pills */}
					<View style={styles.metricsContainer}>
						{availableMetrics.map((metric) => {
							const isSelected = selectedMetrics.includes(metric.id);
							const isDisabled = disableUnselectedMetrics && !isSelected && selectedCount >= maxMetrics;
							return (
								<TouchableOpacity
									key={metric.id}
									style={[
										styles.metricPill,
										isDark && !isSelected && { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
										isSelected && styles.metricPillSelected,
										isDisabled && styles.metricPillDisabled,
									]}
									onPress={() => onMetricToggle(metric.id)}
									disabled={isDisabled}
									activeOpacity={0.7}
								>
									<Text
										style={[
											styles.metricPillText,
											isDark && !isSelected && { color: colors.text.primary },
											isSelected && styles.metricPillTextSelected,
											isDisabled && styles.metricPillTextDisabled,
										]}
									>
										{metric.label}
									</Text>
								</TouchableOpacity>
							);
						})}
					</View>

					{/* Selection Counter */}
					<Text style={[styles.selectionCounter, isDark && { color: colors.text.secondary }]}>
						<Text style={styles.selectionCounterHighlight}>{selectedCount}</Text>
						{' '}of {maxMetrics} selected
					</Text>

					{/* Action Buttons */}
					<View style={styles.actions}>
						<TouchableOpacity
							style={[styles.resetButton, isDark && { backgroundColor: 'transparent', borderColor: colors.brand.primary }]}
							onPress={onReset}
							activeOpacity={0.7}
						>
							<Text style={styles.resetButtonText}>Reset</Text>
						</TouchableOpacity>
						<TouchableOpacity
							style={styles.applyButton}
							onPress={onApply}
							activeOpacity={0.7}
						>
							<Text style={styles.applyButtonText}>Apply</Text>
						</TouchableOpacity>
					</View>
				</View>
			)}
		</View>
	);
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
	container: {
		backgroundColor: '#FFFFFF',
		borderRadius: 8,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.05,
		shadowRadius: 2,
		elevation: 1,
	},
	// Header
	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingHorizontal: 14,
		paddingVertical: 12,
	},
	headerLeft: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 12,
	},
	iconContainer: {
		width: 48,
		height: 48,
		borderRadius: 8,
		backgroundColor: '#005CBC',
		alignItems: 'center',
		justifyContent: 'center',
	},
	headerTextContainer: {
		gap: 2,
	},
	headerTitle: {
		fontSize: 14,
		fontFamily: FontFamily.bold,
		fontWeight: '600',
		color: '#005CBC',
	},
	headerSubtitle: {
		fontSize: 11,
		fontFamily: FontFamily.regular,
		color: '#838383',
	},
	// Content
	content: {
		paddingHorizontal: 14,
		paddingBottom: 12,
		borderTopWidth: 1,
		borderTopColor: '#E9ECEF',
	},
	// View By Section
	sectionLabel: {
		fontSize: 11,
		fontFamily: FontFamily.bold,
		color: '#838383',
		letterSpacing: 0.5,
		marginBottom: 10,
		marginTop: 10,
		fontWeight: '700',
	},
	viewSelector: {
		flexDirection: 'row',
		backgroundColor: '#E9ECEF',
		borderRadius: 8,
		padding: 4,
		marginBottom: 12,
	},
	viewTab: {
		flex: 1,
		paddingVertical: 10,
		paddingHorizontal: 8,
		backgroundColor: 'transparent',
		borderRadius: 6,
		alignItems: 'center',
		justifyContent: 'center',
	},
	viewTabActive: {
		backgroundColor: '#FFFFFF',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.1,
		shadowRadius: 4,
		elevation: 2,
	},
	viewTabText: {
		fontSize: 13,
		fontFamily: FontFamily.medium,
		color: '#616161',
		textAlign: 'center',
		fontWeight: '600',
	},
	viewTabTextActive: {
		color: '#005CBC',
		fontFamily: FontFamily.bold,
	},
	// Instruction
	instruction: {
		fontSize: 11,
		fontFamily: FontFamily.regular,
		fontStyle: 'italic',
		color: '#9E9E9E',
		marginBottom: 12,
		lineHeight: 20,
	},
	// Metrics
	metricsContainer: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 8,
		marginBottom: 10,
	},
	metricPill: {
		paddingVertical: 10,
		paddingHorizontal: 18,
		backgroundColor: '#E9ECEF',
		borderRadius: 24,
		borderWidth: 1,
		borderColor: 'transparent',
	},
	metricPillSelected: {
		backgroundColor: '#005CBC',
		borderColor: '#005CBC',
	},
	metricPillDisabled: {
		opacity: 0.45,
	},
	metricPillText: {
		fontSize: 14,
		fontFamily: FontFamily.medium,
		color: '#6C757D',
	},
	metricPillTextSelected: {
		color: '#FFFFFF',
		fontFamily: FontFamily.bold,
	},
	metricPillTextDisabled: {
		color: '#9AA1AB',
	},
	// Selection Counter
	selectionCounter: {
		fontSize: 14,
		fontFamily: FontFamily.regular,
		color: '#6C757D',
		textAlign: 'right',
		marginBottom: 10,
	},
	selectionCounterHighlight: {
		color: '#005CBC',
		fontFamily: FontFamily.bold,
	},
	// Actions
	actions: {
		flexDirection: 'row',
		gap: 12,
	},
	resetButton: {
		flex: 1,
		paddingVertical: 14,
		backgroundColor: '#FFFFFF',
		borderRadius: 24,
		borderWidth: 1,
		borderColor: '#005CBC',
		alignItems: 'center',
	},
	resetButtonText: {
		fontSize: 14,
		fontFamily: FontFamily.bold,
		color: '#005CBC',
		fontWeight: '700',
	},
	applyButton: {
		flex: 1,
		paddingVertical: 14,
		backgroundColor: '#005CBC',
		borderRadius: 24,
		alignItems: 'center',
	},
	applyButtonText: {
		fontSize: 14,
		fontFamily: FontFamily.bold,
		color: '#FFFFFF',
		fontWeight: '700',
	},
});

ReportBuilder.displayName = 'ReportBuilder';

export default React.memo(ReportBuilder);
